import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  Eye,
  EyeOff,
  Mail,
  KeyRound,
  ArrowLeft,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';

import { Input } from '../../businessSignUp/components/Input';
import { Label } from '../../businessSignUp/components/Label';
import Button from '../../../components/Button';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// ─── Schemas ───────────────────────────────────────────────────────────────────

const emailSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

const resetSchema = z
  .object({
    code: z
      .string()
      .min(1, 'Reset code is required')
      .length(6, 'Code must be exactly 6 characters'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// ─── Component ─────────────────────────────────────────────────────────────────

function ForgotPassword() {
  const server_url = process.env.REACT_APP_API_URI;
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ─── Step 1 form ───────────────────────────────────────────────────────────
  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors, isSubmitting: isEmailSubmitting },
  } = useForm({
    resolver: zodResolver(emailSchema),
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  // ─── Step 2 form ───────────────────────────────────────────────────────────
  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors, isSubmitting: isResetSubmitting },
    reset: resetForm,
  } = useForm({
    resolver: zodResolver(resetSchema),
    mode: 'onChange',
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const onRequestCode = async ({ email }) => {
    try {
      await axios.post(
        `${server_url}/user/sendCode`,
        JSON.stringify({ userEmail: email }),
        { headers: { 'Content-Type': 'application/json' } },
      );
      setSubmittedEmail(email);
      toast.success('Reset code sent! Please check your email.');
      setStep(2);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        'Failed to send reset code. Please try again.';
      toast.error(msg);
    }
  };

  const onResetPassword = async ({ code, newPassword, confirmPassword }) => {
    try {
      await axios.post(
        `${server_url}/user/verifyCode`,
        JSON.stringify({
          userEmail: submittedEmail,
          confirmationCode: code,
          userPW: newPassword,
          confirmPassword,
        }),
        { headers: { 'Content-Type': 'application/json' } },
      );

      toast.success('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        'Failed to reset password. Please check your code and try again.';
      toast.error(msg);
    }
  };

  const handleGoBack = () => {
    setStep(1);
    resetForm();
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary font-montserrat px-4">
        <div className="w-full max-w-md bg-[#111827] shadow-2xl rounded-3xl overflow-hidden border border-slate-800">
          {/* Header Banner */}
          <div className="bg-gradient-to-br from-[#081325] to-[#112140] p-8 md:p-10 text-white relative flex flex-col items-center">
            <Link
              to="/login"
              className="absolute left-6 top-6 flex items-center text-sm gap-1 text-white/70 hover:text-white transition-colors"
            >
              <ArrowBackIosNewIcon fontSize="small" />
              Back to Login
            </Link>

            <div className="mt-6 w-14 h-14 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4">
              {step === 1 ? (
                <Mail className="w-7 h-7 text-blue-400" />
              ) : (
                <ShieldCheck className="w-7 h-7 text-blue-400" />
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-center">
              {step === 1 ? 'Forgot Password?' : 'Reset Your Password'}
            </h1>
            <p className="mt-3 text-blue-200 text-center max-w-xs text-sm">
              {step === 1 ? (
                "Enter your email and we'll send you a reset code."
              ) : (
                <>
                  Enter the code sent to{' '}
                  <span className="font-semibold text-white">
                    {submittedEmail}
                  </span>
                </>
              )}
            </p>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-5">
              <div
                className={`h-1.5 w-12 rounded-full transition-all ${step >= 1 ? 'bg-blue-500' : 'bg-slate-600'}`}
              />
              <div
                className={`h-1.5 w-12 rounded-full transition-all ${step >= 2 ? 'bg-blue-500' : 'bg-slate-600'}`}
              />
            </div>
          </div>

          {/* Body */}
          <div className="p-8 md:p-10">
            {/* ── Step 1: Request Code ── */}
            {step === 1 && (
              <form
                onSubmit={handleSubmitEmail(onRequestCode)}
                className="space-y-6 animate-in slide-in-from-right-4 duration-500"
              >
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    disabled={isEmailSubmitting}
                    {...registerEmail('email')}
                  />
                  {emailErrors.email && (
                    <span className="text-xs text-red-500 font-medium">
                      {emailErrors.email.message}
                    </span>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isEmailSubmitting}
                  className="w-full flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all font-semibold py-3 shadow-lg shadow-blue-500/30"
                >
                  {isEmailSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Reset Code <Mail className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* ── Step 2: Verify Code + New Password ── */}
            {step === 2 && (
              <form
                onSubmit={handleSubmitReset(onResetPassword)}
                className="space-y-6 animate-in slide-in-from-right-4 duration-500"
              >
                {/* Code input */}
                <div className="space-y-2">
                  <Label htmlFor="code">
                    Reset Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter 6-character code"
                    maxLength={6}
                    disabled={isResetSubmitting}
                    className="tracking-[0.3em] uppercase font-mono text-center text-lg"
                    {...registerReset('code', {
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase();
                      },
                    })}
                  />
                  {resetErrors.code && (
                    <span className="text-xs text-red-500 font-medium">
                      {resetErrors.code.message}
                    </span>
                  )}
                </div>

                {/* New password */}
                <div className="space-y-2 relative">
                  <Label htmlFor="newPassword">
                    New Password <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      disabled={isResetSubmitting}
                      className="pr-10"
                      {...registerReset('newPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-300"
                    >
                      {showPassword ? (
                        <Eye className="w-5 h-5" />
                      ) : (
                        <EyeOff className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {resetErrors.newPassword && (
                    <span className="text-xs text-red-500 font-medium">
                      {resetErrors.newPassword.message}
                    </span>
                  )}
                </div>

                {/* Confirm password */}
                <div className="space-y-2 relative">
                  <Label htmlFor="confirmPassword">
                    Confirm Password <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      disabled={isResetSubmitting}
                      className="pr-10"
                      {...registerReset('confirmPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-300"
                    >
                      {showConfirmPassword ? (
                        <Eye className="w-5 h-5" />
                      ) : (
                        <EyeOff className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {resetErrors.confirmPassword && (
                    <span className="text-xs text-red-500 font-medium">
                      {resetErrors.confirmPassword.message}
                    </span>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleGoBack}
                    disabled={isResetSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition-all font-semibold py-3"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <Button
                    type="submit"
                    disabled={isResetSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all font-semibold py-3 shadow-lg shadow-blue-500/30"
                  >
                    {isResetSubmitting ? (
                      'Resetting...'
                    ) : (
                      <>
                        Reset Password <KeyRound className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Resend code (step 2 only) */}
            {step === 2 && (
              <div className="mt-6 text-center text-sm">
                <span className="text-slate-400">Didn't receive the code?</span>{' '}
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium inline-flex items-center gap-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Resend Code
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
